import axios from 'axios';
import { GCPData, GCPResource, ApiResponse, InfrastructureError } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class GCPService {
  private static instance: GCPService;
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): GCPService {
    if (!GCPService.instance) {
      GCPService.instance = new GCPService();
    }
    return GCPService.instance;
  }

  private async apiRequest<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        console.warn('GCP Service: No auth token - request may fail');
        // Let backend handle auth validation
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/infrastructure/gcp${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // Reduced to 10 seconds
      });
      return response.data;
    } catch (error: any) {
      console.error(`GCP API Error for ${endpoint}:`, error);
      throw {
        code: error.response?.status || 'NETWORK_ERROR',
        message: error.response?.data?.message || error.message || 'Failed to fetch GCP data',
        service: 'gcp',
        timestamp: new Date().toISOString()
      } as InfrastructureError;
    }
  }

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data as T;
    }
    return null;
  }

  private setCachedData<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async getGCEInstances(projectId?: string, zone?: string): Promise<GCPResource[]> {
    const cacheKey = `gce-${projectId || 'default'}-${zone || 'all'}`;
    const cached = this.getCachedData<GCPResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);
    if (zone) params.append('zone', zone);

    const response = await this.apiRequest<GCPResource[]>(`/compute/instances?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch GCE instances');
  }

  async getGKSClusters(projectId?: string, zone?: string): Promise<GCPResource[]> {
    const cacheKey = `gks-${projectId || 'default'}-${zone || 'all'}`;
    const cached = this.getCachedData<GCPResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);
    if (zone) params.append('zone', zone);

    const response = await this.apiRequest<GCPResource[]>(`/container/clusters?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch GKS clusters');
  }

  async getBigQueryDatasets(projectId?: string): Promise<any> {
    const cacheKey = `bigquery-${projectId || 'default'}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);

    const response = await this.apiRequest<any>(`/bigquery/datasets?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch BigQuery datasets');
  }

  async getCloudFunctions(projectId?: string, region?: string): Promise<GCPResource[]> {
    const cacheKey = `functions-${projectId || 'default'}-${region || 'all'}`;
    const cached = this.getCachedData<GCPResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<GCPResource[]>(`/functions?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch Cloud Functions');
  }

  async getCloudStorage(projectId?: string): Promise<GCPResource[]> {
    const cacheKey = `storage-${projectId || 'default'}`;
    const cached = this.getCachedData<GCPResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);

    const response = await this.apiRequest<GCPResource[]>(`/storage/buckets?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch Cloud Storage buckets');
  }

  async getCostData(projectId?: string, timeRange = '7d'): Promise<any> {
    const cacheKey = `cost-${projectId || 'default'}-${timeRange}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);
    params.append('timeRange', timeRange);

    const response = await this.apiRequest<any>(`/billing/cost?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch cost data');
  }

  async getAllData(projectId?: string): Promise<GCPData> {
    try {
      const [
        gceInstances,
        gksClusters,
        bigQueryData,
        cloudFunctions,
        cloudStorage,
        costData
      ] = await Promise.allSettled([
        this.getGCEInstances(projectId),
        this.getGKSClusters(projectId),
        this.getBigQueryDatasets(projectId),
        this.getCloudFunctions(projectId),
        this.getCloudStorage(projectId),
        this.getCostData(projectId)
      ]);

      const gceData = gceInstances.status === 'fulfilled' ? gceInstances.value : [];
      const gksData = gksClusters.status === 'fulfilled' ? gksClusters.value : [];
      const bigQuery = bigQueryData.status === 'fulfilled' ? bigQueryData.value : { datasets: 0, tables: 0, totalSize: '0 B' };
      const functions = cloudFunctions.status === 'fulfilled' ? cloudFunctions.value : [];
      const storage = cloudStorage.status === 'fulfilled' ? cloudStorage.value : [];
      const cost = costData.status === 'fulfilled' ? costData.value : { total: 0, byService: {}, trend: 'stable' };

      return {
        gce: {
          instances: gceData,
          total: gceData.length,
          running: gceData.filter(i => i.status === 'running').length,
          stopped: gceData.filter(i => i.status === 'stopped').length
        },
        gks: {
          clusters: gksData,
          total: gksData.length,
          active: gksData.filter(c => c.status === 'running').length
        },
        bigquery: {
          datasets: bigQuery.datasets || 0,
          tables: bigQuery.tables || 0,
          totalSize: bigQuery.totalSize || '0 B'
        },
        cost
      };
    } catch (error) {
      console.error('Error fetching GCP data:', error);
      throw error;
    }
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const gcpService = GCPService.getInstance();